import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';

import App from './App';
import { store } from './store/store';
import './styles/index.css';

const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;
const isDevelopment = import.meta.env.MODE === 'development';
const isPostHogEnabled = Boolean(posthogKey && posthogHost);

if (isPostHogEnabled) {
  posthog.init(posthogKey, {
    api_host: posthogHost,
    defaults: '2025-05-24',
    disable_surveys: true,
    capture_exceptions: {
      capture_console_errors: true,
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
    },
    error_tracking: {
      captureExtensionExceptions: false,
    },
  });

  if (isDevelopment) {
    posthog.debug();
  }

  type PosthogWithErrorTracking = typeof posthog & {
    errorTracking?: {
      enable?: () => void;
    };
  };

  (posthog as PosthogWithErrorTracking).errorTracking?.enable?.();
}

const AppErrorFallback = () => (
  <div style={{ padding: '2rem', textAlign: 'center' }}>
    Что-то пошло не так. Пожалуйста, обновите страницу.
  </div>
);

type ConsoleLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

type ConsoleLogEntry = {
  level: ConsoleLevel;
  message: string;
  timestamp: string;
};

const consoleLogBuffer: ConsoleLogEntry[] = [];
const MAX_CONSOLE_LOGS = 50;
const CONSOLE_PATCH_FLAG = '__consoleHistoryWrapped';

const stringifyConsoleArgs = (args: unknown[]): string =>
  args
    .map((arg) => {
      if (typeof arg === 'string') {
        return arg;
      }

      try {
        const serialized = JSON.stringify(arg);
        return serialized === undefined ? String(arg) : serialized;
      } catch (error) {
        return String(arg);
      }
    })
    .join(' ');

const pushConsoleLog = (level: ConsoleLevel, args: unknown[]) => {
  consoleLogBuffer.push({
    level,
    message: stringifyConsoleArgs(args),
    timestamp: new Date().toISOString(),
  });

  if (consoleLogBuffer.length > MAX_CONSOLE_LOGS) {
    consoleLogBuffer.splice(0, consoleLogBuffer.length - MAX_CONSOLE_LOGS);
  }
};

(['log', 'info', 'warn', 'error', 'debug'] as const).forEach((level) => {
  const original = console[level];

  if (typeof original !== 'function' || (original as { [CONSOLE_PATCH_FLAG]?: boolean })[CONSOLE_PATCH_FLAG]) {
    return;
  }

  console[level] = (...args: unknown[]) => {
    pushConsoleLog(level, args);
    original.apply(console, args as Parameters<typeof original>);
  };

  (console[level] as { [CONSOLE_PATCH_FLAG]?: boolean })[CONSOLE_PATCH_FLAG] = true;
});

type RootErrorBoundaryProps = {
  children: React.ReactNode;
};

type RootErrorBoundaryState = {
  hasError: boolean;
};

class RootErrorBoundary extends React.Component<RootErrorBoundaryProps, RootErrorBoundaryState> {
  state: RootErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RootErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    if (isPostHogEnabled) {
      const stack = error instanceof Error ? error.stack : undefined;
      const consoleLogs = consoleLogBuffer.map((entry) => ({ ...entry }));

      posthog.captureException?.(error, {
        stack,
        componentStack: errorInfo.componentStack,
        handled: true,
        consoleLogs,
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return <AppErrorFallback />;
    }

    return this.props.children;
  }
}

const routedApp = (
  <BrowserRouter>
    <Routes>
      <Route path={'/*'} element={<App />} />
    </Routes>
  </BrowserRouter>
);

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <Provider store={store}>
    <React.StrictMode>
      {isPostHogEnabled ? (
        <PostHogProvider client={posthog}>
          <RootErrorBoundary>{routedApp}</RootErrorBoundary>
        </PostHogProvider>
      ) : (
        routedApp
      )}
    </React.StrictMode>
  </Provider>,
);
